import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { Link, useNavigate } from 'react-router-dom'

import routes from '../../routers'
import {
  addRecipeToCookbook,
  clickPromo,
  dismissPromo,
  getBookProgress,
  getPromos,
} from '../../api/promos'

import styles from './CookbookPromo.module.css'

// Cookbook merchandising (Brief 7). Each placement shows only when its switch is on for the
// person and they haven't closed it; every click is tracked (a Klaviyo event).

let cached = null
let loading = null
const listeners = new Set()

const loadPromos = (force = false) => {
  if (cached && !force) return Promise.resolve(cached)
  if (!loading || force) {
    loading = getPromos()
      .then(({ data }) => {
        cached = data
        listeners.forEach((fn) => fn(data))
        return data
      })
      .catch(() => null)
      .finally(() => {
        loading = null
      })
  }
  return loading
}

export const usePromo = (placement) => {
  const signedIn = useSelector((state) => !!state.user?.data?.id)
  const [config, setConfig] = useState(cached)
  useEffect(() => {
    if (!signedIn) return undefined
    listeners.add(setConfig)
    loadPromos().then((c) => c && setConfig(c))
    return () => listeners.delete(setConfig)
  }, [signedIn])
  const show = signedIn && !!config?.show?.[placement]
  const dismiss = () => {
    cached = cached && { ...cached, show: { ...cached.show, [placement]: false } }
    listeners.forEach((fn) => fn(cached))
    dismissPromo(placement).catch(() => null)
  }
  const track = () => clickPromo(placement)
  return { show, config, dismiss, track }
}

const Close = ({ onClick, label = 'Hide this' }) => (
  <button type="button" className={styles.close} aria-label={label} title={label} onClick={onClick}>
    ×
  </button>
)

// The cookbook builder for this family's book (or a new one)
const builderState = (p) => ({
  groupId: p.group_id,
  orderId: p.cookbook_id || undefined,
  currentStep: p.cookbook_id ? 2 : 1,
})

const Meter = ({ progress }) => {
  const ready = progress.recipes >= progress.needed
  return (
    <div className={styles.meter}>
      <span
        style={{ width: `${Math.min(100, (100 * progress.recipes) / progress.needed)}%` }}
        className={ready ? styles.full : ''}
      />
    </div>
  )
}

const progressText = (p) =>
  p.recipes >= p.needed
    ? `${p.group_name} has ${p.recipes} recipes, enough for a printed cookbook.`
    : `${p.recipes} of ${p.needed} recipes for ${p.group_name}’s printed cookbook.`

// "8 of 12 recipes for your book", after saving a recipe or adding a scan batch
export const BookProgress = ({ groupId, recipeId }) => {
  const navigate = useNavigate()
  const { show, dismiss, track } = usePromo('progress')
  const [progress, setProgress] = useState(null)
  useEffect(() => {
    if (!show) return
    getBookProgress(groupId, recipeId)
      .then(({ data }) => setProgress(data || null))
      .catch(() => setProgress(null))
  }, [show, groupId, recipeId])
  if (!show || !progress?.group_name) return null
  return (
    <div className={styles.card}>
      <div className={styles.body}>
        <p className={styles.title}>{progressText(progress)}</p>
        <Meter progress={progress} />
      </div>
      <button
        type="button"
        className={styles.cta}
        onClick={() => {
          track()
          navigate(routes.purchaseBook, { state: builderState(progress) })
        }}
      >
        {progress.recipes >= progress.needed ? 'Make the cookbook' : 'See the cookbook'}
      </button>
      <Close onClick={dismiss} />
    </div>
  )
}

// On the family group page
export const GroupBanner = ({ groupId }) => {
  const navigate = useNavigate()
  const { show, dismiss, track } = usePromo('group_banner')
  const [progress, setProgress] = useState(null)
  useEffect(() => {
    if (!show || !groupId) return
    getBookProgress(groupId)
      .then(({ data }) => setProgress(data))
      .catch(() => setProgress(null))
  }, [show, groupId])
  if (!show || !progress || !progress.recipes) return null
  return (
    <div className={`${styles.card} ${styles.banner}`}>
      <div className={styles.body}>
        <p className={styles.title}>
          Turn {progress.group_name}’s {progress.recipes} recipes into a printed cookbook
        </p>
        <p className={styles.text}>
          A hardcover or kitchen-ready coil book, printed and shipped to your door.
        </p>
      </div>
      <button
        type="button"
        className={styles.cta}
        onClick={() => {
          track()
          navigate(routes.purchaseBook, { state: builderState(progress) })
        }}
      >
        Make a cookbook
      </button>
      <Close onClick={dismiss} />
    </div>
  )
}

// "Add to cookbook" on a recipe page
export const AddToCookbook = ({ recipeId, groupId }) => {
  const { show, dismiss, track } = usePromo('recipe_button')
  const [added, setAdded] = useState(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  if (!show) return null
  const add = () => {
    track()
    setBusy(true)
    setError('')
    addRecipeToCookbook(recipeId, groupId)
      .then(({ data }) => setAdded(data))
      .catch((e) => setError(e?.response?.data?.data || 'We could not add it. Please try again.'))
      .finally(() => setBusy(false))
  }
  return (
    <div className={styles.addRow}>
      {added ? (
        <p className={styles.added}>
          {added.already_in ? 'Already in' : 'Added to'} your {added.group_name} cookbook (
          {added.in_book} of {added.needed} recipes).{' '}
          <Link to={routes.purchaseBook} state={builderState(added)}>
            Open the cookbook
          </Link>
        </p>
      ) : (
        <>
          <button type="button" className={styles.addButton} onClick={add} disabled={busy}>
            {busy ? 'Adding…' : '+ Add to cookbook'}
          </button>
          <Close onClick={dismiss} label="Hide “Add to cookbook”" />
        </>
      )}
      {error ? <p className={styles.error}>{error}</p> : null}
    </div>
  )
}

// On the PDF export screen (Brief 6)
export const PdfUpsell = () => {
  const navigate = useNavigate()
  const { show, dismiss, track } = usePromo('pdf_upsell')
  if (!show) return null
  return (
    <div className={styles.card}>
      <div className={styles.body}>
        <p className={styles.title}>Want it on paper?</p>
        <p className={styles.text}>
          We can print this as a real cookbook and ship it to you or someone you love.
        </p>
      </div>
      <button
        type="button"
        className={styles.cta}
        onClick={() => {
          track()
          navigate(routes.purchaseBook)
        }}
      >
        Print a cookbook
      </button>
      <Close onClick={dismiss} />
    </div>
  )
}

// Days left to order for Christmas (top of every page, in the last weeks)
export const ChristmasCountdown = () => {
  const navigate = useNavigate()
  const { show, config, dismiss, track } = usePromo('countdown')
  if (!show || config?.days_left === null || config?.days_left === undefined) return null
  const days = config.days_left
  return (
    <div className={styles.countdown} role="status">
      <span>
        {days === 0
          ? 'Today is the last day to order your cookbook for Christmas.'
          : `${days} day${days === 1 ? '' : 's'} left to order your cookbook for Christmas.`}
      </span>
      <button
        type="button"
        className={styles.countdownCta}
        onClick={() => {
          track()
          navigate(routes.purchaseBook)
        }}
      >
        Make yours
      </button>
      <Close onClick={dismiss} />
    </div>
  )
}

export const refreshPromos = () => loadPromos(true)
